import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { usePatientVitalRanges, type VitalRange } from '@/hooks/usePatientVitalRanges';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Activity,
  Plus,
  Pencil,
  Trash2,
  Heart,
  Droplets,
  Thermometer,
  Wind,
  Weight,
  HeartPulse,
} from 'lucide-react';
import { toast } from 'sonner';

const VITAL_TYPES = [
  { value: 'blood_pressure', unit: 'mmHg' },
  { value: 'glucose', unit: 'mg/dL' },
  { value: 'temperature', unit: '°C' },
  { value: 'oxygen_saturation', unit: '%' },
  { value: 'weight', unit: 'kg' },
  { value: 'heart_rate', unit: 'bpm' },
];

const getVitalTypeKey = (type: string) => {
  switch (type) {
    case 'blood_pressure': return 'bloodPressure';
    case 'glucose': return 'glucose';
    case 'temperature': return 'temperature';
    case 'oxygen_saturation': return 'oxygenSaturation';
    case 'weight': return 'weight';
    case 'heart_rate': return 'heartRate';
    default: return type;
  }
};

const getVitalIcon = (type: string) => {
  switch (type) {
    case 'blood_pressure': return <Heart className="w-5 h-5" />;
    case 'glucose': return <Droplets className="w-5 h-5" />;
    case 'temperature': return <Thermometer className="w-5 h-5" />;
    case 'oxygen_saturation': return <Wind className="w-5 h-5" />;
    case 'weight': return <Weight className="w-5 h-5" />;
    case 'heart_rate': return <HeartPulse className="w-5 h-5" />;
    default: return <Activity className="w-5 h-5" />;
  }
};

const formatRange = (range: VitalRange) => {
  if (range.vital_type === 'blood_pressure') {
    const sys = `${range.min_value ?? '?'}–${range.max_value ?? '?'}`;
    const dia = range.min_value_secondary != null && range.max_value_secondary != null
      ? ` / ${range.min_value_secondary}–${range.max_value_secondary}`
      : '';
    return `${sys}${dia} ${range.unit || 'mmHg'}`;
  }
  return `${range.min_value ?? '?'} – ${range.max_value ?? '?'} ${range.unit || ''}`;
};

interface VitalRangesManagerProps {
  patientId: string;
  canEdit: boolean;
  /**
   * 'card' (default): compact card embedded in another page (e.g. PatientDetail, AddTask dialog).
   * 'list': full-page list with empty state and prominent Add button (used by VitalRangesList page).
   */
  variant?: 'card' | 'list';
}

export function VitalRangesManager({ patientId, canEdit, variant = 'card' }: VitalRangesManagerProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { ranges, isLoading, refetch } = usePatientVitalRanges(patientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRange, setEditingRange] = useState<VitalRange | null>(null);

  // Form state
  const [vitalType, setVitalType] = useState('blood_pressure');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [minValueSecondary, setMinValueSecondary] = useState('');
  const [maxValueSecondary, setMaxValueSecondary] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const existingTypes = ranges.map(r => r.vital_type);
  const availableTypes = VITAL_TYPES.filter(
    vt => !existingTypes.includes(vt.value) || editingRange?.vital_type === vt.value
  );
  const canAdd = canEdit && availableTypes.length > existingTypes.length;

  const openAdd = () => {
    setEditingRange(null);
    const firstAvailable = availableTypes.find(vt => !existingTypes.includes(vt.value));
    setVitalType(firstAvailable?.value || 'blood_pressure');
    setMinValue('');
    setMaxValue('');
    setMinValueSecondary('');
    setMaxValueSecondary('');
    setDialogOpen(true);
  };

  const openEdit = (range: VitalRange) => {
    setEditingRange(range);
    setVitalType(range.vital_type);
    setMinValue(range.min_value?.toString() || '');
    setMaxValue(range.max_value?.toString() || '');
    setMinValueSecondary(range.min_value_secondary?.toString() || '');
    setMaxValueSecondary(range.max_value_secondary?.toString() || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);

    const unit = VITAL_TYPES.find(vt => vt.value === vitalType)?.unit || null;
    const payload = {
      patient_id: patientId,
      vital_type: vitalType as any,
      min_value: parseFloat(minValue) || null,
      max_value: parseFloat(maxValue) || null,
      min_value_secondary: vitalType === 'blood_pressure' ? (parseFloat(minValueSecondary) || null) : null,
      max_value_secondary: vitalType === 'blood_pressure' ? (parseFloat(maxValueSecondary) || null) : null,
      unit,
      created_by: user.id,
    };

    try {
      if (editingRange) {
        const { error } = await supabase
          .from('patient_vital_ranges')
          .update(payload as any)
          .eq('id', editingRange.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('patient_vital_ranges')
          .insert(payload as any);
        if (error) throw error;
      }

      toast.success(t('common.success'));
      setDialogOpen(false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    } catch (error: any) {
      console.error('Error saving vital range:', error);
      toast.error(error.message || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (range: VitalRange) => {
    if (!confirm(t('vitalRanges.confirmDelete', { defaultValue: 'Tem certeza que deseja remover este limite?' }))) return;
    try {
      const { error } = await supabase
        .from('patient_vital_ranges')
        .delete()
        .eq('id', range.id);
      if (error) throw error;
      toast.success(t('common.success'));
      refetch();
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    } catch (error: any) {
      toast.error(error.message || t('common.error'));
    }
  };

  if (isLoading) {
    if (variant === 'list') {
      return (
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      );
    }
    return null;
  }

  const dialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingRange
              ? t('vitalRanges.editRange', { defaultValue: 'Editar Limite' })
              : t('vitalRanges.addRange', { defaultValue: 'Adicionar Limite' })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('carePlan.vitalType', { defaultValue: 'Tipo de Sinal Vital' })}</Label>
            <Select value={vitalType} onValueChange={setVitalType} disabled={!!editingRange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(editingRange ? VITAL_TYPES : availableTypes).map(vt => (
                  <SelectItem key={vt.value} value={vt.value}>
                    {t(`vitals.${getVitalTypeKey(vt.value)}`)} ({vt.unit})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {vitalType === 'blood_pressure' ? (
            <>
              <p className="text-xs text-muted-foreground">{t('vitals.systolic', { defaultValue: 'Sistólica' })}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('carePlan.min')}</Label>
                  <Input type="number" placeholder="90" value={minValue} onChange={e => setMinValue(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('carePlan.max')}</Label>
                  <Input type="number" placeholder="140" value={maxValue} onChange={e => setMaxValue(e.target.value)} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('vitals.diastolic', { defaultValue: 'Diastólica' })}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('carePlan.min')}</Label>
                  <Input type="number" placeholder="60" value={minValueSecondary} onChange={e => setMinValueSecondary(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('carePlan.max')}</Label>
                  <Input type="number" placeholder="90" value={maxValueSecondary} onChange={e => setMaxValueSecondary(e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('carePlan.alert')} {t('carePlan.min')}</Label>
                <Input type="number" step="0.1" placeholder="80" value={minValue} onChange={e => setMinValue(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('carePlan.alert')} {t('carePlan.max')}</Label>
                <Input type="number" step="0.1" placeholder="120" value={maxValue} onChange={e => setMaxValue(e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (variant === 'list') {
    return (
      <div className="space-y-4">
        {canAdd && (
          <Button className="w-full" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-2" />
            {t('vitalRanges.addRange', { defaultValue: 'Adicionar limite' })}
          </Button>
        )}

        {ranges.length > 0 ? (
          <div className="space-y-2">
            {ranges.map(range => (
              <Card
                key={range.id}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => canEdit && openEdit(range)}
              >
                <CardContent className="p-4 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-info/10 text-info shrink-0">
                      {getVitalIcon(range.vital_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">
                        {t(`vitals.${getVitalTypeKey(range.vital_type)}`)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatRange(range)}
                      </p>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(range);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(range);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Activity className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold mb-2">
                {t('vitalRanges.noRanges', { defaultValue: 'Nenhum limite configurado' })}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('vitalRanges.noRangesDescription', { defaultValue: 'Configure limites para sinais vitais e receba alertas automáticos quando os valores estiverem fora da faixa.' })}
              </p>
              {canAdd && (
                <Button onClick={openAdd}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('vitalRanges.addRange', { defaultValue: 'Adicionar limite' })}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {dialog}
      </div>
    );
  }

  // Default: compact card variant
  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-info" />
              {t('patient.vitalRanges', { defaultValue: 'Limites de Sinais Vitais' })}
            </CardTitle>
            {canAdd && (
              <Button variant="ghost" size="sm" onClick={openAdd}>
                <Plus className="w-4 h-4 mr-1" />
                {t('common.add')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {ranges.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('patient.noVitalRanges', { defaultValue: 'Nenhum limite configurado. Adicione limites para receber alertas automáticos.' })}
            </p>
          ) : (
            <div className="space-y-2">
              {ranges.map(range => (
                <div key={range.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm font-medium">
                      {t(`vitals.${getVitalTypeKey(range.vital_type)}`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatRange(range)}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(range)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(range)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {dialog}
    </>
  );
}
